import { cx } from '@emotion/css';
import { ArrowDropDownRounded, ArrowDropUpRounded, FiberManualRecord, InfoOutlined } from '@mui/icons-material';
import { Divider, Rating, Tooltip } from '@sortlist-frontend/design-system/legacy';
import { Trans, useTranslation } from '@sortlist-frontend/translation/ssr';

import { usePublicAppContext } from '_core/context/public-app-context';
import { getExpertiseDetails } from '_features/AgencyProfile/components/ServicesPanel/Expertise';
import * as S from '_features/AgencyProfile/components/ServicesPanel/styles';
import { agencyProfileConfig } from '_features/AgencyProfile/config';

import type { ServiceNode } from './types';

interface Props {
  service: ServiceNode;
  servicesCount: number;
  totalWorksCount: number;
  toggled: boolean;
  hasInfo: boolean;
  handlePanelToggle: VoidFunction;
  className?: string;
}

const getExperienceLevel = (worksInService: number, servicesCount: number, totalWorksCount: number) => {
  const average = Math.round(Math.min(((5 * servicesCount) / totalWorksCount) * worksInService, 5));
  if (worksInService === 0) return 1;
  if (worksInService < 3) return Math.ceil(1 + average);
  if (worksInService >= 3 && worksInService < 5) return Math.ceil(2 + average);
  if (worksInService >= 5 && worksInService < 10) return Math.ceil(3 + average);
  if (worksInService >= 10 && worksInService < 15) return Math.ceil(4 + average);
  if (worksInService >= 15 && worksInService < 20) return Math.ceil(5 + average);
  return 10;
};

export const ServicePreview = (props: Props) => {
  const { service, servicesCount, totalWorksCount, toggled, hasInfo, className } = props;
  const { t } = useTranslation(agencyProfileConfig.i18nNamespaces);
  const { locale } = usePublicAppContext();
  const worksCount = service.works.totalCount;
  const experienceLevel = getExperienceLevel(worksCount, servicesCount, totalWorksCount);
  const expertiseUuid = service.expertise.uuid;
  const serviceName = service.expertise.name ?? '';
  const budgetMin = Array.isArray(service.budget) ? service.budget[0] : null;

  return (
    <S.PreviewButton
      hasinfo={hasInfo ? 1 : 0}
      buttonVariant="default"
      animation={!hasInfo ? 'none' : 'ripple'}
      onClick={props.handlePanelToggle}
      className="flex-100 bg-neutral-50">
      <div className={cx(className, 'layout-row layout-align-start-stretch flex-1 py-3 px-2')}>
        <div className="py-1 px-2 layout-column layout-align-center-center">
          <S.ExpertiseIcon className="bg-secondary-300 rounded-full layout-column layout-align-center-center p-2 flex-noshrink">
            {getExpertiseDetails(t, expertiseUuid)?.icon}
          </S.ExpertiseIcon>
        </div>
        <div className="py-1 px-2 layout-row layout-align-start-center text-left flex-1">
          <span className="bold h6 text-truncate">{serviceName}</span>
          <Tooltip
            arrow
            title={
              <div className="layout-column">
                <span className="bold mb-2 p">
                  <Trans i18nKey={'agency:profile.services.serviceInfoTitle'}>{{ serviceName }}, what is it?</Trans>
                </span>
                <p className="small">{getExpertiseDetails(t, expertiseUuid)?.description}</p>
              </div>
            }>
            <InfoOutlined className="m-1 text-secondary-500" style={{ fontSize: 14 }} />
          </Tooltip>
        </div>

        <div className="py-1 mx-4 layout-row layout-align-start-center hide show-gt-sm flex-20">
          <div className="layout-row">
            {Array.from(Array(experienceLevel), (number, index) => (
              <FiberManualRecord key={`1-${number}+${index}`} style={{ fontSize: 8 }} />
            ))}
            {Array.from(Array(10 - experienceLevel), (number, index) => {
              return (
                <FiberManualRecord
                  key={`2-${number}+${index}`}
                  style={{ fontSize: 8 }}
                  className="text-secondary-300"
                />
              );
            })}
          </div>
          <span className="small mx-2 flex-1 text-truncate text-left">
            {worksCount > 0 ? (
              <span className="underline">
                <Trans i18nKey={'agency:profile.services.experiencePreview'} count={worksCount}>
                  {{ count: worksCount }} works
                </Trans>
              </span>
            ) : (
              <span>{t('agency:profile.services.noExperience')}</span>
            )}
          </span>
        </div>

        <Divider className="hide show-gt-sm" />

        <div className="text-left py-1 px-4 layout-column layout-align-center-start hide show-gt-sm flex-15">
          <Rating rate={(service.reviews && (service.reviews.averageRating ?? 0) * 5) || 0} size={14}>
            <span className="small m-1">({service.reviews.totalCount || 0})</span>
          </Rating>
        </div>

        <Divider className="hide show-gt-sm" />

        <div className="py-1 px-4 layout-column hide show-gt-sm flex-15 text-left layout-align-center-start">
          {budgetMin != null && budgetMin > 0 ? (
            <span className="small width-100 text-truncate">
              <Trans
                i18nKey={'agency:profile.services.budgetProjectPreview'}
                values={{
                  budget: new Intl.NumberFormat(locale, {
                    style: 'currency',
                    currency: 'EUR',
                    minimumFractionDigits: 0,
                  }).format(budgetMin),
                }}>
                <span className="bold">{'{{budget}}'}</span>
                <span>/project</span>
              </Trans>
            </span>
          ) : (
            <span className="small width-100">
              <span className="text-secondary-500">{t('agency:profile.services.noBudgetPreview')}</span>
            </span>
          )}
        </div>

        <div className="py-1 px-4 layout-column layout-align-center-center">
          {hasInfo ? (
            toggled ? (
              <ArrowDropUpRounded style={{ fontSize: 24 }} />
            ) : (
              <ArrowDropDownRounded style={{ fontSize: 24 }} />
            )
          ) : (
            <div className="p-3"></div>
          )}
        </div>
      </div>
    </S.PreviewButton>
  );
};
